import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Navigation } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import Frame from "@/components/Frame";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const frameY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative bg-midnight text-white overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: gridY }} className="absolute inset-0 blueprint-dark opacity-60" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-16 md:pt-28 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.05}>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">
              Entreprise de couverture — Champigny-sur-Marne
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.95] text-[1.7rem] sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <MaskedLine delay={0.15}>MSC Toitures —</MaskedLine>
            <MaskedLine delay={0.28}>Votre couvreur dans</MaskedLine>
            <MaskedLine delay={0.41}>
              <span>le <span className="text-signal">Val-de-Marne</span></span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.65, 0, 0.35, 1] }}
            className="mt-8 text-base md:text-lg text-white/70 leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            Réparation, rénovation et entretien de toiture — déplacement et devis gratuits.
            Intervention dans tout le Val-de-Marne (94), de Champigny-sur-Marne à Créteil,
            Saint-Maur-des-Fossés et toutes les communes du département.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.65, 0, 0.35, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="tel:0646300391"
              data-testid="hero-call-button"
              className="btn-sweep inline-flex items-center justify-center gap-3 whitespace-nowrap bg-signal text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={1.5} />
              06 46 30 03 91
            </a>
            <Link
              to="/#contact"
              data-testid="hero-quote-button"
              className="btn-sweep btn-sweep-light inline-flex items-center justify-center gap-3 whitespace-nowrap border border-white/40 text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              Demander un devis gratuit
            </Link>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=34+boulevard+de+Polangis,+94500+Champigny-sur-Marne"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-directions-button"
              className="btn-sweep btn-sweep-light inline-flex items-center justify-center gap-3 whitespace-nowrap border border-white/40 text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              <Navigation size={18} strokeWidth={1.5} />
              Itinéraire showroom
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-trust-row"
          >
            {["Déplacement gratuit", "Devis gratuit", "Intervention dans tout le 94"].map((t) => (
              <span key={t} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                <span className="w-1.5 h-1.5 bg-signal" />
                {t}
              </span>
            ))}
          </motion.div>
        </div>
        <motion.div
          style={{ y: frameY }}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.65, 0, 0.35, 1] }}
          className="lg:col-span-5"
        >
          <Frame
            dark
            label="PHOTO COUVREUR EN INTERVENTION"
            ratio="aspect-[4/5] sm:aspect-[4/4] lg:aspect-[4/5]"
            src="/showroom.webp"
            alt="Showroom MSC Toitures — 34 boulevard de Polangis, Champigny-sur-Marne"
          />
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30 text-right">
            Showroom — 34 bd de Polangis, Champigny-sur-Marne
          </p>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pb-10 hidden md:flex"
      >
        <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
          <ArrowDown size={14} strokeWidth={1.5} className="animate-bounce" />
          Découvrir nos services
        </span>
      </motion.div>
    </section>
  );
}
